import { UserErrorType, UserRole } from "@next-platform/packages-ts-types";
import { useCallback, useState } from "react";
import appLogger from "../../infra/logger/appLogger";
import { compareErrorCode, getNsfwGatingReason, getSubscriptionTier } from "../../utils";
import { USERNAME_TAKEN_ERROR_MESSAGE } from "../constants";
import { useAppDispatch, useAppSelector } from "../hook";
import { useTranslation } from "../localization/hooks";
import { ModerationFlows, useModerationErrors } from "../miscellaneous";
import { PermissionsBanTypes, SubscriptionStatus, UserFlow, UserRoles } from "./types";
import { getUserModeration as getUserModerationApi, resetUserProfile, setAuthenticationStatus, setAuthUser, setError, setFeatureFlags, setGuestUserId, setIsProfileModified, setIsSucceess, setIsUserAuthenticated, setIsUserAuthLoaded, setUserPermissions, setUserRoles, setUserTapTrackingId, updateUserProfile, } from "./userSlice";
const userInfoLogger = appLogger.withTag("UserInfo");
export default function useUserInfo() {
    const dispatch = useAppDispatch();
    const userState = useAppSelector((state) => state?.userInfo);
    const { getMessage: getModerationErrorMessage } = useModerationErrors();
    const { t } = useTranslation();
    const [userError, setUserError] = useState(null);
    const permissions = userState?.permissions || [];
    const userProfile = userState?.profile;
    const userRoles = userState?.roles;
    const isAuthenticated = userState?.isAuthenticated;
    const userId = userState?.profile?.id || userState?.guestUserId || null;
    const onlyUserId = userState?.profile?.id || null;
    const guestUserId = userState?.guestUserId || null;
    const name = userProfile?.name || null;
    const username = userProfile?.username || null;
    const email = userProfile?.email || null;
    const avatar_url = userProfile?.avatar_url || null;
    const highlights = userProfile?.highlights || null;
    const openai_api_key = userProfile?.openai_api_key || null;
    const age_verification = userState?.profile?.age_verification;
    const nsfwGatingReason = getNsfwGatingReason(onlyUserId, age_verification?.status);
    // If we have a reason this mean isNsfwGatingEnabled should be true
    // True mean the user should not see any nsfw content
    const isNsfwGatingEnabled = !!nsfwGatingReason;
    const openai_mode = userProfile?.openai_mode || null;
    const platform = userProfile?.subscription?.platform || null;
    const isAuthLoaded = userState?.isAuthLoaded;
    const isProfileLoaded = userState?.isProfileLoaded;
    const isProfileModified = userState?.isProfileModified;
    const authUser = userState?.authUser;
    const tapTrackingId = userState?.tapTrackingId;
    const subscription = userState?.profile?.subscription;
    const isUserFullyRestricted = permissions?.includes(PermissionsBanTypes.RESTRICTION_COMPLETE) || permissions?.includes(PermissionsBanTypes.RESTRICTION_TEMPORARY);
    const isUserLimitedRestricted = permissions?.includes(PermissionsBanTypes.LIMITED_ACCESS);
    const isLoading = userState?.isLoading;
    const isSuccess = userState?.isSuccess;
    const features = userState?.features;
    const getUserRole = (roles) => {
        // The condition will prevent crash when roles is empty.
        const rolesKey = Array.isArray(roles) ? roles.map((role) => role.key) : [];
        // TODO(Arthur): [NX-12253] Unify user roles across brands and replace brand-specific role checks with a shared role definition.
        if (rolesKey.includes(UserRoles.PREMIUM) || rolesKey.includes(UserRole.SM_PREMIUM)) {
            return UserRoles.PREMIUM;
        }
        else if (rolesKey.includes(UserRoles.BASIC) || rolesKey.includes(UserRole.SM_BASIC)) {
            return UserRoles.BASIC;
        }
        else if (rolesKey.includes(UserRoles.MINIMAL)) {
            return UserRoles.MINIMAL;
        }
        else {
            return UserRoles.FREE;
        }
    };
    const checkIfPermissionExists = (permission) => {
        return permissions?.includes(permission);
    };
    const getUserBanType = (permissions) => {
        let banType = PermissionsBanTypes.NONE;
        if (permissions?.includes(PermissionsBanTypes.RESTRICTION_TEMPORARY)) {
            banType = PermissionsBanTypes.RESTRICTION_TEMPORARY;
        }
        else if (permissions?.includes(PermissionsBanTypes.RESTRICTION_COMPLETE)) {
            banType = PermissionsBanTypes.RESTRICTION_COMPLETE;
        }
        else if (permissions?.includes(PermissionsBanTypes.LIMITED_ACCESS)) {
            banType = PermissionsBanTypes.LIMITED_ACCESS;
        }
        return banType;
    };
    const userBanType = getUserBanType(permissions);
    const role = getUserRole(userRoles) || null;
    const subscriptionTier = getSubscriptionTier(role) || null;
    const roles = userRoles?.map((role) => role.key) ?? [];
    const billingCycle = userProfile?.subscription?.billing_cycle;
    const status = role !== UserRoles.FREE ? SubscriptionStatus.ACTIVE : SubscriptionStatus.EXPIRED;
    const isInvalid = userState.isInvalid;
    const getUserModeration = async () => {
        return dispatch(getUserModerationApi()).unwrap();
    };
    // The following functions are wrapper functions for dispatching actions.
    const setIsAuthenticated = useCallback((payload) => {
        dispatch(setIsUserAuthenticated(payload));
    }, [setIsUserAuthenticated]);
    const setPermission = useCallback((payload) => {
        dispatch(setUserPermissions(payload));
    }, [setUserPermissions]);
    const setUserAuthData = useCallback((payload) => {
        dispatch(setAuthUser(payload));
    }, [setAuthUser]);
    const setRoles = useCallback((payload) => {
        dispatch(setUserRoles(payload));
    }, [setUserRoles]);
    const resetProfile = useCallback(() => {
        dispatch(resetUserProfile());
    }, [resetUserProfile]);
    const setIsUserProfileModified = useCallback((payload) => {
        dispatch(setIsProfileModified(payload));
    }, [setIsProfileModified]);
    const setIsAuthLoaded = useCallback((payload) => {
        dispatch(setIsUserAuthLoaded(payload));
    }, [setIsUserAuthLoaded]);
    const setTapTrackingId = useCallback((payload) => {
        dispatch(setUserTapTrackingId(payload));
    }, [setUserTapTrackingId]);
    const setGuestId = useCallback((payload) => {
        dispatch(setGuestUserId(payload));
    }, [setGuestUserId]);
    /** backward compatible for native - to be removed after native done with error handling. Please use onUserUpdateProfileV2 */
    const onUserUpdateProfile = useCallback(async (payload) => {
        return await dispatch(updateUserProfile(payload)).unwrap();
    }, [updateUserProfile]);
    // error handling + updated return structure to avoid unhandled exceptions
    const onUserUpdateProfileV2 = useCallback(async (payload) => {
        try {
            setUserError(null);
            const data = await dispatch(updateUserProfile(payload)).unwrap();
            return { data, error: null };
        }
        catch (error) {
            const apiError = error;
            const errorCode = apiError?.errorCode;
            const moderationErrorMessage = getModerationErrorMessage({
                errorCode,
                skipDefaultMessage: true,
                errorContext: {
                    flow: ModerationFlows.UPDATE_USER,
                },
            });
            let errorMessage = moderationErrorMessage || apiError?.message || t("error:fallbackError");
            setUserError({
                errorCode,
                errorMessage,
                errorType: UserFlow.UPDATE_PROFILE,
            });
            const isUserNameAlreadyTakenError = compareErrorCode(errorCode, UserErrorType.USERNAME_DUPLICATED);
            // only log error when if it's not a duplicate username error
            if (isUserNameAlreadyTakenError) {
                errorMessage = USERNAME_TAKEN_ERROR_MESSAGE;
            }
            else {
                userInfoLogger.error(`error while updating user: ${errorMessage}`, apiError);
            }
            return { data: null, error: errorMessage, errorPayload: apiError };
        }
    }, [updateUserProfile, getModerationErrorMessage]);
    const resetForNextUse = () => {
        setIsSucceess(false);
        setError(null);
    };
    const setUserFeatureFlags = useCallback((payload) => {
        dispatch(setFeatureFlags(payload));
    }, [setFeatureFlags]);
    const setUserAuthenticationStatus = (authLoaded, isAuthenticated) => {
        dispatch(setAuthenticationStatus({ authLoaded, isAuthenticated }));
    };
    return {
        userProfile,
        userId,
        name,
        username,
        email,
        avatar_url,
        role,
        subscriptionTier,
        platform,
        roles,
        permissions,
        features,
        guestUserId,
        onlyUserId,
        authUser,
        tapTrackingId,
        subscription,
        highlights,
        openai_api_key,
        age_verification,
        isNsfwGatingEnabled,
        nsfwGatingReason,
        openai_mode,
        isUserFullyRestricted,
        isUserLimitedRestricted,
        userBanType,
        isAuthenticated,
        isProfileLoaded,
        isProfileModified,
        status,
        isInvalid,
        isAuthLoaded,
        isLoading,
        isSuccess,
        onUserUpdateProfile,
        getUserModeration,
        setIsAuthenticated,
        setPermission,
        setUserAuthData,
        setRoles,
        resetProfile,
        setIsUserProfileModified,
        setIsAuthLoaded,
        setTapTrackingId,
        setGuestId,
        resetForNextUse,
        setUserFeatureFlags,
        checkIfPermissionExists,
        getSubscriptionTier,
        billingCycle,
        setUserAuthenticationStatus,
        onUserUpdateProfileV2,
        errorMessage: userError?.errorMessage || null,
        errorCode: userError?.errorCode || null,
        errorType: userError?.errorType || null,
    };
}
